import { useState, ReactNode, MouseEvent, useEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { PortalContext } from "../widget/WidgetProvider";

interface CursorTooltipProps {
  children: ReactNode;
  /**
   * Tooltip body. A plain string is wrapped in a <p> (the original behavior);
   * a ReactNode is rendered as-is so callers can show a richer card
   * (NETSOL-12004: MDS DOB/Payor/Location/Reference Window hover-over).
   */
  content: ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

export function CursorTooltip({ children, content, onClick, className, style }: CursorTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fadeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Portal into the widget's shadow root (where the scoped Tailwind styles and
  // CSS variables live). Portaling to document.body escapes the shadow root and
  // renders the tooltip unstyled/invisible. Falls back to document.body when
  // running standalone (no shadow root / PortalContext is null).
  const portalContainer = useContext(PortalContext);
  const portalTarget: DocumentFragment | HTMLElement =
    portalContainer ?? (typeof document !== "undefined" ? document.body : (null as never));

  const clearAllTimers = () => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }
  };

  const handleMouseMove = (e: MouseEvent<HTMLTableRowElement>) => {
    setPosition({
      x: e.clientX,
      y: e.clientY,
    });

    // Clear existing timers when mouse moves
    clearAllTimers();
    setIsVisible(false);
    setIsFadingOut(false);

    // Start new timer to show tooltip after 0.5 seconds of no movement
    showTimeoutRef.current = setTimeout(() => {
      setIsVisible(true);
      setIsFadingOut(false);

      // Set timer to start fade out after 2 seconds
      hideTimeoutRef.current = setTimeout(() => {
        setIsFadingOut(true);
        
        // Actually hide after fade animation completes (300ms)
        fadeTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
          setIsFadingOut(false);
        }, 300);
      }, 2000);
    }, 500);
  };

  const handleMouseEnter = () => {
    // Reset state on enter
    setIsVisible(false);
    setIsFadingOut(false);
  };

  const handleMouseLeave = () => {
    clearAllTimers();
    setIsVisible(false);
    setIsFadingOut(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, []);

  return (
    <>
      <tr
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className={className}
        style={style}
      >
        {children}
      </tr>
      {isVisible && createPortal(
        <div
          className={`fixed z-50 bg-popover text-popover-foreground border border-border rounded-[var(--radius)] px-3 py-1.5 pointer-events-none shadow-[var(--elevation-sm)] transition-opacity duration-300 ${
            isFadingOut ? "opacity-0" : "opacity-100"
          }`}
          style={{
            left: `${position.x + 12}px`,
            top: `${position.y + 12}px`,
          }}
        >
          {typeof content === "string" ? <p>{content}</p> : content}
        </div>,
        portalTarget
      )}
    </>
  );
}